import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';

import type { Editor as TinymceEditor } from 'tinymce';

import { ReportFragmentAction } from 'webapp/enums/enums';
import { stripAllHtml } from 'webapp/utils/html';

import type { HtmlEditorOptions } from 'webapp/components/form/html-editor';
import FormHtmlEditorComponent from 'webapp/components/form/html-editor';
import type { ReportFragmentActionData } from 'webapp/components/reports/editor-base';
import type { Args as BaseArgs } from 'webapp/components/reports/viewer/fragment/title';
import ReportsViewerFragmentTitleComponent from 'webapp/components/reports/viewer/fragment/title';

import { DEFAULT_REPORT_TITLE } from 'webapp/models-custom/report';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';
import { DEFAULT_SLIDE_TITLE } from 'webapp/models/report-slide';

interface Args extends BaseArgs {
	onFragmentChange: (
		fragment: ReportSlideFragment,
		action: ReportFragmentAction,
		data?: ReportFragmentActionData,
	) => Promise<unknown>;
}

export default class ReportsEditorFragmentTitleComponent extends ReportsViewerFragmentTitleComponent<Args> {
	isInputFocused = false;
	currentFragmentText: string | null = null;
	currentReportTitle: string | null = null;
	currentSlideTitle: string | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.args.model.isNewlyCreated) {
			this.isInputFocused = true;
			this.args.model.isNewlyCreated = false;
		}
	}

	get placeholder() {
		return this.isPagelessFirstCoverTitle ? DEFAULT_REPORT_TITLE : 'Header';
	}

	get slide() {
		return this.args.model.slide;
	}

	get isFirstHeaderOnSlide() {
		return this.slide?.headerFragment?.reportSlideId.toString() === this.slide?.id;
	}

	get report() {
		return this.slide?.report;
	}

	get isPageless() {
		return this.report?.isPageless;
	}

	get centerByDefault() {
		return !!this.args.model.slide?.isTypeCover && !this.isPageless;
	}

	get options(): HtmlEditorOptions {
		const options: HtmlEditorOptions = {
			placeholder: this.placeholder,
			toolbar: false,
			quickbars_selection_toolbar:
				'fontselect | forecolor bold italic underline | alignleft aligncenter alignright | removeformat link emoticons',
			plugins: ['paste', 'wordcount', 'link', 'emoticons', 'quickbars'],
			contextmenu: false,
			// paste plugin
			paste_block_drop: true,
			smart_paste: false,
			paste_as_text: true,
			isDistractionFree: true,
			onSetup: this.onEditorSetup,
			// link plugin
			link_assume_external_targets: 'https',
			link_default_protocol: 'https',
			link_title: false,
			target_list: false,
		};

		options.forced_root_block = 'div';

		options.centerByDefault = this.centerByDefault;
		options.isInputFocused = this.isInputFocused;

		return options;
	}

	get isCoverTitle() {
		return this.args.model.slide?.isTypeCover;
	}

	get isPagelessCoverTitle() {
		return this.isCoverTitle && this.isPageless;
	}

	get isPagelessFirstCoverTitle() {
		return (
			this.isPagelessCoverTitle && this.args.model?.slide?.id === this.report?.firstSlide!.id
		);
	}

	@action
	changeTitle(text: string): void {
		const report = this.report;
		const currentText = stripAllHtml(text);

		if (!report) {
			return;
		}

		if (text !== this.text && text !== this.placeholder) {
			const fragment = this.args.model;
			fragment.updateText(text);

			// update report title if it matched the initial fragment text before editing
			if (this.isPagelessFirstCoverTitle && this.currentReportTitle && !!currentText) {
				if (
					this.currentReportTitle === DEFAULT_REPORT_TITLE ||
					this.currentFragmentText === this.currentReportTitle
				) {
					fragment.updateChangeText(currentText);
					report.directPropUpdate({ title: currentText });
				}
			}

			void this.args.onFragmentChange(fragment, ReportFragmentAction.CONTENT);
		}
	}

	@action
	onEditorSetup(_component: unknown, editor: TinymceEditor): void {
		if (editor && typeof editor.on === 'function') {
			editor.on('focus', () => {
				this.currentFragmentText = stripAllHtml(this.args.model.value.content ?? '');
				this.currentReportTitle = this.report?.title || null;
				this.currentSlideTitle = this.slide?.title || null;
			});

			// delay is needed to allow direct button clicks (like "Add Emoji"/"Add Cover") after content has been changed
			editor.on('blur', () => {
				later(() => {
					this.handleTitleEditingFinished(editor.getContent());
				}, 100);
			});
		}
	}

	private handleTitleEditingFinished(text: string): void {
		const slide = this.slide;
		const currentText = stripAllHtml(text);

		// update slide title if it matched the initial fragment text before editing
		if (slide && this.isFirstHeaderOnSlide && currentText?.trim()) {
			if (
				!slide.title?.trim() ||
				slide.title === DEFAULT_SLIDE_TITLE ||
				this.currentSlideTitle === this.currentFragmentText
			) {
				slide.title = currentText;
				void slide.saveAndCatch();
			}
		}
	}
	<template>
		<div class="fragment fragment--title">
			<h2 class="user-html">
				<FormHtmlEditorComponent
					@text={{this.text}}
					@onChange={{this.changeTitle}}
					@options={{this.options}}
					@maxChars={{300}}
				/>
			</h2>
		</div>
	</template>
}
